"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { LoginRequiredModal } from "@/components/core/LoginRequiredModal";
import { useAuth } from "@/context/auth";
import { useFetchBlogComments } from "@/hooks/blog/useFetchBlogComments";
import { CommentForm } from "./CommentForm";
import { CommentItem, type CommentNode } from "./CommentItem";
import { CommentListSkeleton } from "./CommentListSkeleton";

const FA = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const faNum = (n: number) => String(n).replace(/[0-9]/g, (d) => FA[Number(d)]);

export const BlogComments: React.FC<{ slug: string }> = ({ slug }) => {
  const t = useTranslations("blog");
  const isLoggedIn = useAuth((s) => s.isLoggedIn);
  const [loginOpen, setLoginOpen] = useState(false);
  const query = useFetchBlogComments(slug);
  const { ref, inView } = useInView();
  const listKey = ["blog-comments", slug, 20];

  useEffect(() => {
    if (inView && !query.isFetchingNextPage && query.hasNextPage) {
      query.fetchNextPage();
    }
  }, [
    inView,
    query.isFetchingNextPage,
    query.hasNextPage,
    query.fetchNextPage,
  ]);

  const items: CommentNode[] =
    query.data?.pages.flatMap((p) => p?.data?.result ?? []) ?? [];
  const total =
    (query.data?.pages[0]?.data?.summary?.total_count as number | undefined) ??
    0;

  return (
    <section className="mt-8 border-t border-stroke-light/40 pt-6">
      <h2 className="mb-6 text-lg font-bold text-text-primary">
        {t("comments.heading")} ({faNum(total)})
      </h2>

      <CommentForm
        slug={slug}
        isLoggedIn={isLoggedIn}
        onRequireLogin={() => setLoginOpen(true)}
      />

      <div className="mt-8 space-y-6">
        {query.isLoading ? (
          <CommentListSkeleton />
        ) : (
          items.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              slug={slug}
              listKey={listKey}
              isLoggedIn={isLoggedIn}
              onRequireLogin={() => setLoginOpen(true)}
            />
          ))
        )}
        <div ref={ref}>
          {query.isFetchingNextPage && <CommentListSkeleton />}
        </div>
      </div>

      <LoginRequiredModal open={loginOpen} onOpenChange={setLoginOpen} />
    </section>
  );
};
